// src/routes/knowledgeBase.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';

interface KBUploadProps {
  token: string;
}

interface KnowledgeBaseDetail {
  id: number;
  name?: string;
  description?: string;
  client?: string | null;
  ipAddress?: string | null;
  browser?: string | null;
  userAgent?: string | null;
  embedProvider?: string | null;
  embedModel?: string | null;
  updatedAt?: string;
  metadata?: Record<string, unknown> | null;
  sources?: Array<{
    id: number;
    sourceType: 'file' | 'url';
    originalFilename?: string | null;
    url?: string | null;
    version?: number | null;
  }>;
}

type ProviderModel = { name: string; notes?: string };
type ProviderOption = {
  id: 'hf' | 'openai';
  label: string;
  recommended_models: ProviderModel[];
  default_model: string;
};



const KBUpload: React.FC<KBUploadProps> = ({ token }) => {
  const [searchParams] = useSearchParams();
  // ---- Sources ----
  const [files, setFiles] = useState<File[]>([]);
  const [urlsText, setUrlsText] = useState<string>('');
  const [parsedUrls, setParsedUrls] = useState<string[]>([]);

  // ---- KB meta ----
  const [kbName, setKbName] = useState('');
  const [kbDescription, setKbDescription] = useState('');

  // ---- UX state ----
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  // ---- Providers/Models ----
  const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [provider, setProvider] = useState<'hf' | 'openai'>('hf');
  const [model, setModel] = useState<string>('all-MiniLM-L6-v2');
  const [updateKbId, setUpdateKbId] = useState<number | null>(null);
  const [updateSourceId, setUpdateSourceId] = useState<number | null>(null);
  const [mode, setMode] = useState<'replace-kb' | 'replace-source' | 'add-source' | 'create'>('create');
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBaseDetail | null>(null);

  // Fetch providers on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingProviders(true);
  const res = await api.get(`/kb/embedding-providers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const provs: ProviderOption[] = res.data?.providers || [];
        if (!mounted) return;
        setProviderOptions(provs);

        const hf = provs.find((p) => p.id === 'hf');
        const initialProvider = (hf?.id as 'hf' | 'openai') || 'hf';
        setProvider(initialProvider);
        setModel(hf?.default_model || 'all-MiniLM-L6-v2');
      } catch (e: any) {
        console.warn('Failed to load embedding providers, falling back to defaults.', e?.message);
      } finally {
        if (mounted) setLoadingProviders(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  // Update mode from query param
  useEffect(() => {
    const rawKb = searchParams.get('kbId');
    const rawSource = searchParams.get('sourceId');
    const rawMode = searchParams.get('mode');
    if (!rawKb) {
      setUpdateKbId(null);
      setUpdateSourceId(null);
      setMode('create');
      return;
    }
    const kbId = Number(rawKb);
    const sourceId = rawSource ? Number(rawSource) : null;
    setUpdateKbId(Number.isNaN(kbId) ? null : kbId);
    setUpdateSourceId(sourceId && !Number.isNaN(sourceId) ? sourceId : null);
    if (sourceId && !Number.isNaN(sourceId)) {
      setMode('replace-source');
    } else if (rawMode === 'add-source') {
      setMode('add-source');
    } else {
      setMode('replace-kb');
    }
  }, [searchParams]);

  // Prefill when updating a KB
  useEffect(() => {
    if (!updateKbId) {
      setSelectedKnowledgeBase(null);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/api/kb/read/${updateKbId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const kb = res.data?.knowledgeBase as KnowledgeBaseDetail | undefined;
        if (!mounted || !kb) return;
        setSelectedKnowledgeBase(kb);
        if (!kbName) setKbName(kb.name || '');
        if (!kbDescription) setKbDescription(kb.description || '');
        if (kb.embedProvider === 'hf' || kb.embedProvider === 'openai') {
          setProvider(kb.embedProvider);
        }
        if (kb.embedModel) setModel(kb.embedModel);
      } catch (e: any) {
        console.warn('Failed to load KB for update.', e?.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [updateKbId, token, kbName, kbDescription]);

  const selectedSource = useMemo(
    () => selectedKnowledgeBase?.sources?.find((source) => source.id === updateSourceId) || null,
    [selectedKnowledgeBase, updateSourceId]
  );
  const isSourceReplaceMode = mode === 'replace-source' && Boolean(updateKbId && updateSourceId);
  const isSourceAddMode = mode === 'add-source' && Boolean(updateKbId && !updateSourceId);
  const isKbReplaceMode = mode === 'replace-kb' && Boolean(updateKbId && !updateSourceId);

  const formatDate = (dateValue?: string) => {
    if (!dateValue) return 'N/A';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString();
  };

  // Models for current provider
  const currentProvider = useMemo(
    () => providerOptions.find((p) => p.id === provider),
    [provider, providerOptions]
  );
  const currentModels = currentProvider?.recommended_models || [
    { name: 'all-MiniLM-L6-v2', notes: '384-dim, fast baseline' },
  ];
  const currentDefaultModel = currentProvider?.default_model || 'all-MiniLM-L6-v2';
  const pageTitle = isSourceReplaceMode
    ? 'Replace Knowledge Base Source'
    : isSourceAddMode
      ? 'Add Sources to Knowledge Base'
    : updateKbId
      ? 'Update Knowledge Base'
      : 'Build a Knowledge Base';
  const submitLabel = isSourceReplaceMode
    ? 'Replace This Source'
    : isSourceAddMode
      ? 'Add Sources to This Knowledge Base'
    : updateKbId
      ? 'Update Knowledge Base from Files/URLs'
      : 'Create Knowledge Base from Files/URLs';

  // Keep model sane when provider changes
  useEffect(() => {
    setModel(currentDefaultModel);
  }, [provider, currentDefaultModel]);

  // ---------------- File helpers ----------------
  const supportedTypes = {
    text: ['.txt'],
    pdf: ['.pdf'],
    word: ['.doc', '.docx'],
    markdown: ['.md'],
    image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    video: ['.mp4', '.avi', '.mov', '.wmv'],
  };

  const getAllSupportedExtensions = () => Object.values(supportedTypes).flat().join(', ');

  const getFileTypeCategory = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    for (const [category, extensions] of Object.entries(supportedTypes)) {
      if (extensions.includes(extension)) return category;
    }
    return 'text';
  };

  const isFileTypeSupported = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return Object.values(supportedTypes).flat().includes(extension);
  };

  const maxSizeForCategoryMB = (cat: string) => (cat === 'video' ? 50 : 10);
  const maxSizeForFile = (file: File) => maxSizeForCategoryMB(getFileTypeCategory(file.name)) * 1024 * 1024;

  const handleFilesAdd = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const newWarnings: string[] = [];
    const next: File[] = [...files];

    Array.from(fileList).forEach((f) => {
      if (!isFileTypeSupported(f.name)) {
        newWarnings.push(`Unsupported type: ${f.name}. Allowed: ${getAllSupportedExtensions()}`);
        return;
      }
      const limit = maxSizeForFile(f);
      if (f.size > limit) {
        newWarnings.push(
          `Too large: ${f.name}. Limit ${Math.round(limit / (1024 * 1024))}MB for ${getFileTypeCategory(f.name)}`
        );
        return;
      }
      // avoid duplicates by name+size
      if (!next.find((x) => x.name === f.name && x.size === f.size)) {
        next.push(f);
      }
    });

    setFiles(next);
    // Default KB name from first file if empty
    if (!kbName && next.length > 0) {
      const first = next[0];
      const nameWithoutExtension = first.name.includes('.') ? first.name.replace(/\.[^/.]+$/, '') : first.name;
      setKbName(nameWithoutExtension);
    }
    setWarnings(newWarnings);
    setSuccess(false);
    setMessage('');
  };

  const removeFile = (idx: number) => {
    const next = files.slice();
    next.splice(idx, 1);
    setFiles(next);
  };

  const clearFiles = () => setFiles([]);

  const totalSizeMB = useMemo(
    () => (files.reduce((a, f) => a + f.size, 0) / (1024 * 1024)).toFixed(2),
    [files]
  );

  // ---------------- URL helpers ----------------
  const parseUrls = (raw: string): string[] => {
    const s = (raw || '').trim();
    if (!s) return [];
    try {
      const maybe = JSON.parse(s);
      if (Array.isArray(maybe)) {
        return Array.from(new Set(maybe.map((u) => String(u).trim()).filter(Boolean)));
      }
    } catch {
      // fall through
    }
    const out: string[] = [];
    s.replace(/\r/g, '')
      .split('\n')
      .forEach((line) => {
        line
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((u) => out.push(u));
      });
    return Array.from(new Set(out));
  };

  useEffect(() => {
    setParsedUrls(parseUrls(urlsText));
    if (!kbName && urlsText.trim()) {
      // derive name from first URL hostname
      const first = parseUrls(urlsText)[0];
      if (first) {
        try {
          const u = new URL(first);
          setKbName(u.hostname.replace(/^www\./, ''));
        } catch {
          // ignore
        }
      }
    }
  }, [urlsText]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------- Submit ----------------
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isSourceReplaceMode && !isSourceAddMode && !kbName.trim()) {
      setMessage('Please provide a name for your knowledge base');
      setSuccess(false);
      return;
    }
    if (files.length === 0 && parsedUrls.length === 0) {
      setMessage('Please add at least one file or one URL');
      setSuccess(false);
      return;
    }
    if (isSourceReplaceMode) {
      const providedKinds = (files.length > 0 ? 1 : 0) + (parsedUrls.length > 0 ? 1 : 0);
      if (providedKinds !== 1 || files.length > 1 || parsedUrls.length > 1) {
        setMessage('Single-source replacement requires exactly one file or one URL');
        setSuccess(false);
        return;
      }
    }

    setUploading(true);
    setMessage('');
    setSuccess(false);

    const formData = new FormData();
    files.forEach((f) => formData.append('file', f)); // multiple files under same field name
    if (parsedUrls.length > 0) {
      formData.append('urls', JSON.stringify(parsedUrls)); // backend accepts JSON array (also supports CSV/lines)
    }

    if (!isSourceReplaceMode && !isSourceAddMode) {
      formData.append('name', kbName.trim());
      if (kbDescription.trim()) formData.append('description', kbDescription.trim());
    }
    formData.append('provider', provider);
    formData.append('model', model);

    try {
      const endpoint = isSourceReplaceMode
        ? `/kb/source/${updateSourceId}`
        : isSourceAddMode
          ? `/kb/source/${updateKbId}`
        : updateKbId
          ? `/kb/update/${updateKbId}`
          : `/kb/upload`;
      const method = isSourceReplaceMode
        ? api.put
        : isSourceAddMode
          ? api.post
          : updateKbId
            ? api.put
            : api.post;
      const res = await method(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      setMessage(
        res.data.message ||
          (isSourceReplaceMode
            ? 'Source updated successfully! Only that document was re-indexed.'
            : isSourceAddMode
            ? 'New sources added successfully! Existing documents stayed indexed.'
            : updateKbId
            ? 'Update successful! Knowledge base has been re-indexed.'
            : 'Upload successful! Knowledge base has been indexed and is ready to use.')
      );
      setSuccess(true);
      setFiles([]);
      setUrlsText('');
      setKbName('');
      setKbDescription('');
      setWarnings([]);
      const fileInput = document.getElementById('file-upload') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      console.error('Upload error:', err);
      setMessage(err?.response?.data?.message || 'Upload failed. Please try again.');
      setSuccess(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          {pageTitle}
        </h2>

        <form onSubmit={handleUpload} className="space-y-6">
          {isKbReplaceMode && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-lg text-sm">
              This will replace all existing files and URLs for this knowledge base.
            </div>
          )}
          {isSourceAddMode && (
            <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg text-sm">
              This will add new files or URLs to the existing knowledge base without rebuilding the old sources.
            </div>
          )}
          {isSourceReplaceMode && selectedSource && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-lg text-sm">
              Replacing source #{selectedSource.id}:{' '}
              {selectedSource.sourceType === 'file'
                ? (selectedSource.originalFilename || 'Unnamed file')
                : (selectedSource.url || 'Unnamed URL')}
              {typeof selectedSource.version === 'number' ? ` (current version v${selectedSource.version})` : ''}
            </div>
          )}
          {updateKbId && selectedKnowledgeBase && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
              <p className="text-sm font-semibold text-gray-900 mb-2">Last Update Metadata</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <p><span className="font-semibold text-gray-900">Client:</span> {selectedKnowledgeBase.client || 'vsknowledgebase'}</p>
                <p><span className="font-semibold text-gray-900">Updated:</span> {formatDate(selectedKnowledgeBase.updatedAt)}</p>
                <p><span className="font-semibold text-gray-900">IP:</span> {selectedKnowledgeBase.ipAddress || 'N/A'}</p>
                <p><span className="font-semibold text-gray-900">Browser:</span> {selectedKnowledgeBase.browser || 'N/A'}</p>
              </div>
              {selectedKnowledgeBase.userAgent ? (
                <p className="mt-2 truncate" title={selectedKnowledgeBase.userAgent}>
                  <span className="font-semibold text-gray-900">User-Agent:</span> {selectedKnowledgeBase.userAgent}
                </p>
              ) : null}
            </div>
          )}
          {/* Knowledge Base Name */}
          <div>
            <label htmlFor="kb-name" className="block text-sm font-medium text-gray-700 mb-2">
              Knowledge Base Name *
            </label>
            <input
              id="kb-name"
              type="text"
              value={kbName}
              onChange={(e) => setKbName(e.target.value)}
              disabled={uploading || isSourceReplaceMode || isSourceAddMode}
              placeholder={(isSourceReplaceMode || isSourceAddMode) ? 'KB name is unchanged during source-level operations' : 'Enter a descriptive name for your knowledge base'}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="kb-description" className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              id="kb-description"
              value={kbDescription}
              onChange={(e) => setKbDescription(e.target.value)}
              disabled={uploading || isSourceReplaceMode || isSourceAddMode}
              placeholder={(isSourceReplaceMode || isSourceAddMode) ? 'KB description is unchanged during source-level operations' : 'Brief description of the knowledge base content'}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              maxLength={500}
            />
            <p className="mt-1 text-sm text-gray-500">{kbDescription.length}/500 characters</p>
          </div>

          {/* Provider & Model */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="provider" className="block text-sm font-medium text-gray-700 mb-2">
                Embedding Provider *
              </label>
              <select
                id="provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value as 'hf' | 'openai')}
                disabled={uploading || loadingProviders || isSourceReplaceMode || isSourceAddMode}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                {(providerOptions.length
                  ? providerOptions
                  : ([
                      { id: 'hf', label: 'HuggingFace (local)' },
                      { id: 'openai', label: 'OpenAI (hosted)' },
                    ] as any)
                ).map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              {loadingProviders && <p className="mt-1 text-xs text-gray-500">Loading providers…</p>}
            </div>

            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                Embedding Model *
              </label>
              <select
                id="model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={uploading || loadingProviders || isSourceReplaceMode || isSourceAddMode}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                {currentModels.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {currentModels.find((m) => m.name === model)?.notes || 'Select a recommended model'}
              </p>
            </div>
          </div>

          {/* Sources: Files */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700">
                Add Files
              </label>
              {files.length > 0 && (
                <button
                  type="button"
                  onClick={clearFiles}
                  className="text-sm text-blue-600 hover:underline"
                  disabled={uploading}
                >
                  Clear all
                </button>
              )}
            </div>
            <input
              id="file-upload"
              type="file"
              multiple
              accept=".txt,.pdf,.doc,.docx,.md,.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.wmv"
              onChange={(e) => handleFilesAdd(e.target.files)}
              disabled={uploading}
              className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="mt-2 text-sm text-gray-500">
              Supported: .txt, .pdf, .doc, .docx, .md, images (.jpg, .png, .gif, .webp), videos (.mp4, .avi, .mov, .wmv).
              Limits: 10MB (docs/images), 50MB (videos).
            </p>

            {files.length > 0 && (
              <div className="mt-3 bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Selected Files ({files.length})</h3>
                  <span className="text-xs text-gray-600">Total size: {totalSizeMB} MB</span>
                </div>
                <ul className="mt-2 divide-y divide-gray-200">
                  {files.map((f, idx) => (
                    <li key={`${f.name}-${f.size}-${idx}`} className="py-2 flex items-center justify-between">
                      <div className="text-sm text-gray-700 truncate">
                        <span className="font-medium">{f.name}</span>
                        <span className="ml-2 text-gray-500">
                          ({(f.size / 1024).toFixed(0)} KB · {getFileTypeCategory(f.name)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="text-sm text-red-600 hover:underline"
                        disabled={uploading}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sources: URLs */}
          <div>
            <label htmlFor="urls" className="block text-sm font-medium text-gray-700 mb-2">
              Add Website URLs (one per line or comma-separated)
            </label>
            <textarea
              id="urls"
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              disabled={uploading}
              placeholder="https://docs.example.com/guide&#10;https://blog.example.com/post-1"
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
            />
            <div className="mt-1 text-sm text-gray-600">
              {parsedUrls.length > 0 ? (
                <span>{parsedUrls.length} URL{parsedUrls.length > 1 ? 's' : ''} detected</span>
              ) : (
                <span className="opacity-75">Tip: paste multiple URLs; we’ll fetch and index them together.</span>
              )}
            </div>
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-lg">
              <ul className="list-disc list-inside text-sm space-y-1">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={
              uploading || (!isSourceReplaceMode && !isSourceAddMode && !kbName.trim()) || (files.length === 0 && parsedUrls.length === 0)
            }
            className={`w-full py-3 px-4 rounded-lg font-semibold transition ${
              uploading || (!isSourceReplaceMode && !isSourceAddMode && !kbName.trim()) || (files.length === 0 && parsedUrls.length === 0)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Processing & Indexing...</span>
              </div>
            ) : (
              submitLabel
            )}
          </button>

          {/* Result message */}
          {message && (
            <div
              className={`p-4 rounded-lg ${
                success
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {success ? (
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">{message}</p>
                  {success && <p className="mt-1 text-sm opacity-75">You can now test your knowledge base in the Playground!</p>}
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Help panels */}
        <div className="mt-8 bg-blue-50 rounded-lg p-4">
          <h3 className="font-medium text-blue-800 mb-2">📖 How it works:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>1. Choose embedding provider & model</li>
            <li>2. Provide a name and optional description</li>
            <li>3. Add one or more files and/or paste URLs</li>
            <li>4. We’ll fetch, clean, chunk, and index everything into a single KB</li>
            <li>5. Test your knowledge base in the Playground</li>
          </ul>
        </div>

        <div className="mt-4 bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-2">📄 Supported Inputs:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p><strong>Documents:</strong></p>
              <ul className="list-disc list-inside ml-2">
                <li>Text files (.txt)</li>
                <li>PDF documents (.pdf)</li>
                <li>Word documents (.doc, .docx)</li>
                <li>Markdown files (.md)</li>
              </ul>
            </div>
            <div>
              <p><strong>Media:</strong></p>
              <ul className="list-disc list-inside ml-2">
                <li>Images (.jpg, .png, .gif, .webp)</li>
                <li>Videos (.mp4, .avi, .mov, .wmv)</li>
              </ul>
              <p className="mt-2"><strong>Websites:</strong> Paste one URL per line or comma-separated</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { KBUpload };
export default KBUpload;
